import { EMEARegion, NARegion } from "@dana/Common/Constants";
import { formatDateWithDash } from "@dana/Common/utils";
import InvoiceRecepients from "@dana/Components/OrderHistory/InvoiceRecepients";
import { PrintButton } from "@dana/Components/PrintButton/PrintButton";
import StyledTable from "@dana/Components/StyledTable/StyledTable";
import { themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { OrderPackingSlip } from "@dana/Types/ApiModels";
import { formatDecimal } from "@dana/utils/utils";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Website } from "@insite/client-framework/Services/WebsiteService";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useRef } from "react";
import { css } from "styled-components";

interface PackingSlipProps {
    invoiceData: OrderPackingSlip | null;
    websiteData: Website;
    selectedRegion: string | undefined;
}

// --- Styles Interfaces and Objects ---
export interface PackingSlipStyles {
    invoiceContainer?: InjectableCss;
}

export const PackingSlipStyles: PackingSlipStyles = {
    invoiceContainer: {
        css: css`
            font-size: ${fontSizes.small};
            line-height: ${lineHeights.small};

            .invoice-actions {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 16px;
                padding: 16px 0;
            }

            table {
                width: 100%;
            }

            td.company-logo {
                width: 200px;
            }

            td.reprint-text {
                width: 130px;
                text-align: right;
                vertical-align: top;
            }

            .address,
            address {
                font-style: normal;
            }

            .invoice-address-table {
                .section-left {
                    width: 45%;
                    padding-right: 30px;
                }

                .section-right {
                    width: 55%;
                }
            }

            .address-table {
                .invoice_card {
                    height: 150px;
                }
            }

            .company-address {
                h3 {
                    font-weight: ${fontWeights.semiBold};
                }
            }

            .printtype-text {
                font-size: ${fontSizes.large};
                line-height: ${lineHeights.large};
                font-weight: ${fontWeights.semiBold};
                color: ${themeColors.primary.danaBlue};
                text-transform: uppercase;
                margin-bottom: 16px;
            }

            .print-text {
                font-weight: ${fontWeights.semiBold};
                text-transform: uppercase;
            }

            .header-table {
                margin-bottom: 24px;
            }
            td.text-right {
                text-align: right;
            }
        `,
    },
};

const styles = PackingSlipStyles;

// --- Component Definition ---
const PackingSlip: FC<PackingSlipProps> = ({ invoiceData, websiteData, selectedRegion }) => {
    const modalContentRef = useRef<HTMLDivElement>(null);
    const totalQuantity = invoiceData?.items?.reduce((sum, item) => sum + (item?.quantityShipped ?? 0), 0) ?? 0;

    const totalNetWeight = invoiceData?.items?.reduce((sum, item) => sum + (item?.unitWeight ?? 0), 0) ?? 0;

    const uniqueCartonNumbers = new Set(invoiceData?.items?.map(item => item.cartonNumber));
    const uniqueCount = uniqueCartonNumbers.size;
    const shipLocation =
        invoiceData?.shippingPoint !== undefined && invoiceData?.shippingPoint !== ""
            ? JSON.parse(invoiceData?.shippingPoint ?? "")
            : "";

    return (
        <StyledWrapper className="invoice-container" {...styles.invoiceContainer} ref={modalContentRef}>
            <div className="invoice-actions text-right">
                {invoiceData !== null && (
                    <InvoiceRecepients invoicetype="slip" invoiceData={invoiceData} contentRef={modalContentRef} />
                )}
                <PrintButton label={translate("Print Payslip")} targetRef={modalContentRef} />
            </div>
            {/* Header */}
            <table className="header-table">
                <tbody>
                    <tr>
                        <td className="company-logo">
                            <img src={websiteData.companyLogoImagePath} alt="Company Logo" className="company-logo" />
                        </td>
                        <td className="company-address">
                            <address>
                                <h3>{invoiceData?.shipPlantName}</h3>
                                <p>
                                    {invoiceData?.shipPlantAddress1}
                                    {invoiceData?.shipPlantCountry && (
                                        <>
                                            <br />
                                            {invoiceData.shipPlantCountry}
                                        </>
                                    )}
                                    {websiteData?.companyUrl && (
                                        <>
                                            <br />
                                            {websiteData.companyUrl}
                                        </>
                                    )}
                                </p>
                            </address>
                        </td>
                        <td className="reprint-text">
                            <Typography forwardAs="p" className="printtype-text">
                                {translate("Packing Slip")}
                            </Typography>
                            <Typography forwardAs="p" className="print-text">
                                {translate("Reprint")}
                            </Typography>
                        </td>
                    </tr>
                </tbody>
            </table>
            
            {selectedRegion === NARegion && (invoiceData?.shipVia || invoiceData?.trackProNumber) && (
                <table className="tracking-package-table">
                    <tbody>
                        <tr>
                            <td>
                                <div className="invoice_card">
                                    <div className="invoice_card_header">
                                        {translate("Track Package")}
                                    </div>
                                    <div className="invoice_card_body">
                                        {invoiceData?.shipVia && (
                                            <div>
                                                <span className="font-semibold">{translate("Carrier")}: </span>
                                                {invoiceData?.shipVia} 
                                            </div>
                                        )}
                                        {invoiceData?.trackProNumber && (
                                            <div>
                                                <span className="font-semibold">{translate("Tracking Number")}: </span>
                                                {invoiceData?.trackProNumber} 
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
            <table className="invoice-address-table">
                <tr>
                    <td valign="top" className="section-left">
                        {/* Sold To and Ship To */}
                        <table className="address-table">
                            <tbody>
                                <tr>
                                    <td>
                                        <div className="invoice_card">
                                            <div className="invoice_card_header">
                                                {" "}
                                                {translate("SOLD TO")}: {invoiceData?.customerSoldTo}
                                            </div>
                                            <div className="invoice_card_body">
                                                <address>
                                                    <p>
                                                        {invoiceData?.soldToCustomerAddress1}
                                                        {invoiceData?.soldToCustomerAddress2 && (
                                                            <>
                                                                <br />
                                                                {invoiceData.soldToCustomerAddress2}
                                                            </>
                                                        )}
                                                        {(
                                                            invoiceData?.soldToCustomerCity ||
                                                            invoiceData?.soldToCustomerState ||
                                                            invoiceData?.soldToCustomerZipCode
                                                        ) && (
                                                                <>
                                                                    <br />
                                                                    {[
                                                                        invoiceData?.soldToCustomerCity,
                                                                        invoiceData?.soldToCustomerState,
                                                                        invoiceData?.soldToCustomerZipCode,
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(", ")}
                                                                </>
                                                            )}
                                                        {invoiceData?.soldToCustomerCountry && (
                                                            <>
                                                                <br />
                                                                {invoiceData.soldToCustomerCountry}
                                                            </>
                                                        )}
                                                    </p>
                                                </address>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td>
                                        <div className="invoice_card">
                                            <div className="invoice_card_header">
                                                {translate("SHIP TO")}: {invoiceData?.customerShipTo}
                                            </div>
                                            <div className="invoice_card_body">
                                                <address>
                                                    <p>
                                                        {invoiceData?.shipToCustomerAddress1}
                                                        {invoiceData?.shipToCustomerAddress2 && (
                                                            <>
                                                                <br />
                                                                {invoiceData.shipToCustomerAddress2}
                                                            </>
                                                        )}
                                                        {(
                                                            invoiceData?.shipToCustomerCity ||
                                                            invoiceData?.shipToCustomerState ||
                                                            invoiceData?.shipToCustomerZipcode
                                                        ) && (
                                                            <>
                                                                <br />
                                                                {[
                                                                    invoiceData?.shipToCustomerCity,
                                                                    invoiceData?.shipToCustomerState,
                                                                    invoiceData?.shipToCustomerZipcode,
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(", ")}
                                                            </>
                                                        )}
                                                        {invoiceData?.shipToCustomerCountry && (
                                                            <>
                                                                <br />
                                                                {invoiceData.shipToCustomerCountry}
                                                            </>
                                                        )}
                                                    </p>
                                                </address>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </td>
                    <td valign="top" className="section-right">
                        {/* Delivery Information */}
                        <table className="delivery-information-table">
                            <div className="invoice_card">
                                <div className="invoice_card_header">{translate("Information")}</div>
                                <div className="invoice_card_body">
                                    <table className="text-body-sm">
                                        <tbody>
                                            <tr>
                                                <td className="font-semibold">{translate("Delivery No.")}</td>
                                                <td>{invoiceData?.deliveryNumber}</td>
                                            </tr>
                                            <tr>
                                                <td className="font-semibold">{translate("Document Date")}</td>
                                                <td>
                                                    {formatDateWithDash(selectedRegion === EMEARegion,invoiceData?.deliveryCreationDate?.toString()?.split('T')[0] || "")}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="font-semibold">{translate("Ship-to-party")}</td>
                                                <td>{invoiceData?.customerShipTo}</td>
                                            </tr>
                                            <tr>
                                                <td className="font-semibold">{translate("Shipping Conditions")}</td>
                                                <td> {invoiceData?.shipInst} </td>
                                            </tr>
                                            <tr>
                                                <td className="font-semibold">{translate("Incoterms")}</td>
                                                <td>{invoiceData?.incoTermsDescription}</td>
                                            </tr>
                                            <tr>
                                                <td className="font-semibold">{translate("Shipping Point")}</td>
                                                <td>
                                                    {shipLocation?.desc},{shipLocation?.city},{shipLocation?.state}
                                                </td>
                                            </tr>
                                            {selectedRegion !== NARegion && (
                                                <tr>
                                                    <td className="font-semibold">{translate("Carrier")}</td>
                                                    <td>{invoiceData?.shipVia}</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <td className="font-semibold">{translate("Supplier ID")}</td>
                                                <td></td>
                                            </tr>
                                            {selectedRegion !== NARegion && (
                                                <tr>
                                                    <td className="font-semibold">{translate("Tracking No.")}</td>
                                                    <td>{invoiceData?.trackProNumber}</td>
                                                </tr>
                                            )}
                                            
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </table>
                    </td>
                </tr>
            </table>

            {/* Line Item Table */}
            <StyledTable className="details-table">
                <thead>
                    <tr>
                        <th>{translate("Carton No")}</th>
                        <th>{translate("Order No")}</th>
                        <th>{translate("Customer PO No")}</th>
                        <th>{translate("Secondary PO No")}</th>
                        <th>{translate("Line No")}</th>
                        <th>{translate("Part No")}</th>
                        <th>{translate("Qty Shipped")}</th>
                        <th>{translate("Country of Origin")}</th>
                        <th>
                            {translate("Weight ")}({invoiceData?.items?.[0].unitOfMeasurement ?? "LB"})
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {invoiceData?.items?.map((item, index) => (
                        <tr key={index}>
                            <td>{item.cartonNumber}</td>
                            <td>{item.sapOrderNumber}</td>
                            <td>{item.sapCustPoNumber}</td>
                            <td>{invoiceData?.secondaryPo}</td>
                            <td>{item.lineNumber}</td>
                            <td>
                                {item.danaPartNumber}
                                <div className="cust-part-no">
                                    {translate("Cust. Part No.")}:{item.customerPartNumber}
                                </div>
                            </td>
                            <td className="text-right">{item.quantityShipped?.toLocaleString()}</td>
                            <td>{item.countryOfOrigin}</td>
                            <td className="text-right">{formatDecimal(String(item.unitWeight))}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="total-row">
                        <td colSpan={9}>
                            {translate("Carton Dimension(L*W*H)")}:{" "}
                            {invoiceData?.items?.[0].boxDimensions ?? " 0.000 X0.000 X0.000 "}
                        </td>
                    </tr>
                    <tr className="total-row">
                        <td colSpan={4}>
                            {translate("Total No. of Carton(s)")}:{uniqueCount}
                        </td>
                        <td colSpan={2}>
                            {translate("Total Quantity Shipped")}:
                        </td>
                        <td className="text-right">{totalQuantity?.toLocaleString()}</td>
                        <td>
                            {translate("Net Weight ")}({invoiceData?.items?.[0].unitOfMeasurement ?? "LB"}):
                        </td>
                        <td className="text-right">{formatDecimal(totalNetWeight)}</td>
                    </tr>
                </tfoot>
            </StyledTable>
        </StyledWrapper>
    );
};

export default PackingSlip;
